/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * @module TechSupportListPageModule
 */

import {
    Component,
    Input,
    OnChanges,
} from '@angular/core';

import { IAviDataGridConfig } from 'ng/modules/data-grid/components/avi-data-grid/avi-data-grid.types';
import { SchemaService } from 'ajs/modules/core/services/schema-service/schema.service';
import { L10nService } from '@vmw/ngx-vip';
import * as globalL10n from 'global-l10n';
import { AviNotificationType } from 'ng/modules/notification';

import {
    IPreChecksInfo,
    ITechSupport,
    ITechSupportEventMap,
    PreCheckFsmState,
    Syserr,
    TechSupportFsmState,
} from 'generated-types';

import './tech-support-wizard-page.component.less';
import * as l10n from './tech-support-wizard-page.l10n';

const { ...globalL10nKeys } = globalL10n;
const { ENGLISH: dictionary, ...l10nKeys } = l10n;
const ALERT_TYPE_ERROR = 'danger';

/**
 * @description Wizard page Component to display Tech Support package progress
 * Progress and transcript of PreCheck and Packaging page.
 * @author Dhanashree Tilak
 */
@Component({
    selector: 'tech-support-wizard-page',
    templateUrl: './tech-support-wizard-page.component.html',
})
export class TechSupportWizardPageComponent implements OnChanges {
    @Input()
    public techSupportPackage: ITechSupport;

    @Input()
    public page = 'preCheck';

    /**
     * Progress percentage of package
     */
    public progress: number;

    /** Pre-check tasks group as per status */
    public groupedPrechecks: Record<string, IPreChecksInfo[]> = {};

    /** Packaging tasks group as per status */
    public groupedTasks: Record<string, ITechSupportEventMap[]> = {};

    public techSupportLevel: ITechSupport[] = [];

    public readonly l10nKeys = l10nKeys;

    public readonly globalL10nKeys = globalL10nKeys;

    public techSupportLevelGridConfig: IAviDataGridConfig;

    /**
     * Transcript to be shown in readonly text-area.
     */
    public transcript: string;

    /**
     * Precheck state hash.
     */
    public readonly preCheckFsmState = {
        ERROR: PreCheckFsmState.PRECHECK_FSM_ERROR,
        IN_PROGRESS: PreCheckFsmState.PRECHECK_FSM_IN_PROGRESS,
        SUCCESS: PreCheckFsmState.PRECHECK_FSM_SUCCESS,
        WARNING: PreCheckFsmState.PRECHECK_FSM_WARNING,
        STARTED: PreCheckFsmState.PRECHECK_FSM_STARTED,
    };

    /**
     * Type of notification to show.
     */
    public alertType: string = AviNotificationType.SUCCESS;

    public alertMessage = '';

    /**
     * Precheck state hash.
     */
    public readonly techSupportState = {
        COMPLETED: TechSupportFsmState.TECHSUPPORT_FSM_COMPLETED,
        COMPLETED_WITH_WARNINGS: TechSupportFsmState.TECHSUPPORT_FSM_COMPLETED_WITH_WARNINGS,
        ERROR: TechSupportFsmState.TECHSUPPORT_FSM_ERROR,
        INPROGRESS: TechSupportFsmState.TECHSUPPORT_FSM_IN_PROGRESS,
        WARNING: TechSupportFsmState.TECHSUPPORT_FSM_WARNING,
    };

    /**
     * Package task state hash.
     */
    public readonly taskFsmStatus = {
        SUCCESS: Syserr.SYSERR_SUCCESS,
    };

    /**
     * Hash of check status and its l10n keys.
     */
    public readonly checkStatusGroupHash = {
        error: this.l10nKeys.errorLabel,
        warning: this.l10nKeys.warningLabel,
        success: this.l10nKeys.successLabel,
    };

    public constructor(
        private readonly l10nService: L10nService,
        private readonly schemaService: SchemaService,

    ) {
        l10nService.registerSourceBundles(dictionary);
    }

    /** @override */
    public ngOnChanges(): void {
        if (this.techSupportPackage) {
            this.transcript = this.getTranscript();
            this.progress = this.getProgress();

            this.setAlertMessage();

            if (this.techSupportPackage.techsupport_readiness?.checks?.length) {
                this.groupedPrechecks = this.techSupportPackage.techsupport_readiness?.checks
                    .reduce((
                        groupedChecks: Record<string, IPreChecksInfo[]>,
                        check: IPreChecksInfo,
                    ) => {
                        switch (check.state) {
                            case PreCheckFsmState.PRECHECK_FSM_SUCCESS:
                                groupedChecks.success.push(check);
                                break;

                            case PreCheckFsmState.PRECHECK_FSM_ERROR:
                                groupedChecks.error.push(check);
                                break;

                            case PreCheckFsmState.PRECHECK_FSM_WARNING:
                                groupedChecks.warning.push(check);
                                break;
                        }

                        return groupedChecks;
                    }, {
                        error: [],
                        warning: [],
                        success: [],
                    });
            }

            if (this.techSupportPackage.tasks?.length) {
                this.groupedTasks = this.techSupportPackage.tasks
                    .reduce((
                        groupedChecks: Record<string, ITechSupportEventMap[]>,
                        check: ITechSupportEventMap,
                    ) => {
                        switch (check.status) {
                            case Syserr.SYSERR_SUCCESS:
                                groupedChecks.success.push(check);
                                break;

                            default:
                                groupedChecks.error.push(check);
                                break;
                        }

                        return groupedChecks;
                    }, {
                        error: [],
                        warning: [],
                        success: [],
                    });
            }
        }
    }

    /** @override */
    public ngOnInit(): void {
        this.techSupportLevel = [{
            name: this.techSupportPackage.name,
            level: this.techSupportPackage.level,
        }];

        const { l10nService } = this;

        this.techSupportLevelGridConfig = {
            fields: [
                {
                    label: l10nService.getMessage(globalL10nKeys.nameLabel),
                    id: 'name',
                    transform: (row: ITechSupport) => row.name,
                },
                {
                    label: l10nService.getMessage(globalL10nKeys.typeLabel),
                    id: 'type',
                    transform: (row: ITechSupport) => row.level,
                },
            ],
            layout: {
                hideCheckboxes: true,
            },
        };
    }

    /**
     * Get transcript as formatted string.
     */
    public getTranscript(): string {
        if (!this.techSupportPackage) {
            return '';
        }

        const content = this.page === 'preCheck' ?
            this.techSupportPackage.techsupport_readiness?.checks : this.techSupportPackage.tasks;

        return content ? JSON.stringify(content, null, 4) : '';
    }

    /**
     * Return the index of the array element for tracking in the template.
     */
    public trackByIndex(index: number): number {
        return index;
    }

    /**
     * Empty method for keyValue pipe to unsort keys.
     */
    public unsorted(): number {
        return 0;
    }

    /**
     * Show progress icon and message when progress is less than 100
     */
    public get showProgressNote(): boolean {
        if (this.page === 'preCheck') {
            return this.techSupportPackage.techsupport_readiness?.state?.state ===
                PreCheckFsmState.PRECHECK_FSM_IN_PROGRESS;
        } else if (this.page === 'packaging') {
            return this.techSupportPackage.state?.state === TechSupportFsmState.TECHSUPPORT_FSM_IN_PROGRESS;
        }

        return false;
    }

    /**
     * Get the progress percentage based on active page
     * Return Progress percentage
     */
    public getProgress(): number {
        if (this.page === 'preCheck') {
            return this.techSupportPackage.techsupport_readiness?.progress || 0;
        } else if (this.page === 'packaging') {
            return this.techSupportPackage.progress || 0;
        }

        return 0;
    }

    /**
     * Set alert notification when Progress is complete.
     */
    public setAlertMessage(): void {
        let status = '';
        let reason = '';

        if (this.page === 'preCheck') {
            status = this.techSupportPackage.techsupport_readiness?.state.state;
            reason = this.techSupportPackage.techsupport_readiness?.state.reason;
        } else if (this.page === 'packaging') {
            status = this.techSupportPackage.state?.state;
            reason = this.techSupportPackage.state?.reason;
        }

        switch (status as string) {
            case PreCheckFsmState.PRECHECK_FSM_ERROR:
                this.alertType = ALERT_TYPE_ERROR;
                this.alertMessage = `${this.l10nService.getMessage(l10nKeys.preCheckFailureMessage)} : ${reason}`;
                break;
            case PreCheckFsmState.PRECHECK_FSM_SUCCESS:
                this.alertType = AviNotificationType.SUCCESS;
                this.alertMessage = this.l10nService.getMessage(l10nKeys.preCheckSuccessMessage);
                break;

            case PreCheckFsmState.PRECHECK_FSM_WARNING:
                this.alertType = AviNotificationType.WARNING;
                this.alertMessage = `${this.l10nService.getMessage(l10nKeys.preCheckWarningMessage)} : ${reason}`;
                break;

            case TechSupportFsmState.TECHSUPPORT_FSM_COMPLETED:
                this.alertType = AviNotificationType.SUCCESS;
                this.alertMessage = this.l10nService.getMessage(l10nKeys.packageSuccessMessage);
                break;

            case TechSupportFsmState.TECHSUPPORT_FSM_COMPLETED_WITH_WARNINGS:
                this.alertType = AviNotificationType.WARNING;
                this.alertMessage = this.l10nService.getMessage(l10nKeys.packageCompletedWithWarningMessage);
                break;

            case TechSupportFsmState.TECHSUPPORT_FSM_ERROR:
                this.alertType = ALERT_TYPE_ERROR;
                this.alertMessage = `${this.l10nService.getMessage(l10nKeys.packageFailureMessage)} : ${reason}`;
                break;

            case TechSupportFsmState.TECHSUPPORT_FSM_WARNING:
                this.alertType = AviNotificationType.WARNING;
                this.alertMessage = `${this.l10nService.getMessage(l10nKeys.packageWarningMessage)} : ${reason}`;
                break;

            default:
                this.alertType = AviNotificationType.SUCCESS;
                this.alertMessage = '';
                break;
        }
    }
}
